import { useState, useCallback, useEffect, useRef } from 'react'
import { parseIntervalToDays } from '~/common/utils/date-utils'

const WEEK_CONTAINER_WIDTH = 32
const PIXELS_PER_DAY = WEEK_CONTAINER_WIDTH / 7  // ~4.57px

/**
 * Custom hook for panning an individual succession child's schedule as a rigid body.
 *
 * When the user clicks and drags a succession phase/task rect, all phases (and tasks)
 * for that specific child shift by the same number of days.
 *
 * @param {Object} options
 * @param {Function} options.onSuccessionPhaseAdjust - (childId, phases) => void
 * @param {Function} options.onSuccessionTaskAdjust - (childId, tasks) => void
 * @returns {Object} { isPanning, panningChildId, handlePanStart }
 */
export const useSuccessionPan = ({ onSuccessionPhaseAdjust, onSuccessionTaskAdjust }) => {
  const [panState, setPanState] = useState({
    isPanning: false,
    childId: null,
    startX: 0,
    startPhases: null,
    startTasks: null,
  })

  const onPhaseAdjustRef = useRef(onSuccessionPhaseAdjust)
  const onTaskAdjustRef = useRef(onSuccessionTaskAdjust)
  const panStateRef = useRef(panState)

  useEffect(() => {
    onPhaseAdjustRef.current = onSuccessionPhaseAdjust
    onTaskAdjustRef.current = onSuccessionTaskAdjust
    panStateRef.current = panState
  }, [onSuccessionPhaseAdjust, onSuccessionTaskAdjust, panState])

  // phases and tasks are week-format objects with DB-format fields (est_started_at, est_duration, task_window_start, duration)
  const handlePanStart = useCallback((childId, phases, tasks, clientX) => {
    if (!phases || phases.length === 0) return
    setPanState({
      isPanning: true,
      childId,
      startX: clientX,
      startPhases: phases.map(p => ({
        ...p,
        _startDuration: parseIntervalToDays(p.est_duration),
      })),
      startTasks: tasks?.map(t => ({ ...t })) ?? null,
    })
  }, [])

  const handlePanMove = useCallback((e) => {
    const state = panStateRef.current
    if (!state.isPanning || !state.startPhases) return

    const deltaX = e.clientX - state.startX
    const deltaDays = Math.round(deltaX / PIXELS_PER_DAY)
    if (deltaDays === 0) return

    const updatedPhases = state.startPhases.map(({ _startDuration, ...phase }) => {
      const newStart = new Date(phase.est_started_at)
      newStart.setDate(newStart.getDate() + deltaDays)
      return { ...phase, est_started_at: newStart.toISOString() }
    })

    if (onPhaseAdjustRef.current) {
      onPhaseAdjustRef.current(state.childId, updatedPhases)
    }

    if (state.startTasks && onTaskAdjustRef.current) {
      const updatedTasks = state.startTasks.map(task => {
        const newStart = new Date(task.task_window_start)
        newStart.setDate(newStart.getDate() + deltaDays)
        return { ...task, task_window_start: newStart.toISOString() }
      })
      onTaskAdjustRef.current(state.childId, updatedTasks)
    }
  }, [])

  const handlePanEnd = useCallback(() => {
    setPanState({ isPanning: false, childId: null, startX: 0, startPhases: null, startTasks: null })
  }, [])

  useEffect(() => {
    if (panState.isPanning) {
      document.addEventListener('mousemove', handlePanMove)
      document.addEventListener('mouseup', handlePanEnd)
      return () => {
        document.removeEventListener('mousemove', handlePanMove)
        document.removeEventListener('mouseup', handlePanEnd)
      }
    }
  }, [panState.isPanning, handlePanMove, handlePanEnd])

  return {
    isPanning: panState.isPanning,
    panningChildId: panState.childId,
    handlePanStart,
  }
}
